import useQuery from './useQuery'

/**
 * Flags de las features beta del team en UN request.
 *
 * Antes cada feature pedía su propio `/<feature>/enabled` y el número crecía
 * con cada beta (la HCE abría con dos, y el modal del odontograma sumaba un
 * tercero para saber si mostrar "Facturar").
 *
 * FALLBACK OBLIGATORIO: `/features` puede no existir todavía en un entorno
 * (el frontend es un bundle único y se deploya independiente del api). Si
 * responde error, se cae a los endpoints individuales, que siguen vivos. Sin
 * esto, un deploy de frontend adelantado al de api apagaría TODAS las betas a
 * la vez — justo lo que un flag no debe poder hacer por accidente.
 *
 * Las queries de fallback quedan deshabilitadas mientras `/features` funcione,
 * así que en el camino feliz es exactamente un request.
 */
export default function useBetaFeatures() {
  // retry: false a propósito. El QueryClient del repo no configura retries, así
  // que el default de react-query v3 son 3 reintentos: en un entorno donde este
  // endpoint todavía no existe, cada apertura de HCE pagaría 4 requests
  // fallidos ANTES de caer al fallback (y después los 3 individuales) — o sea
  // 7 requests donde antes había 2, empeorando justo lo que este hook vino a
  // mejorar. Un 404 no se arregla reintentando, y un 500 transitorio lo cubre
  // el fallback igual.
  const featuresQuery = useQuery(window._l('/features').substr(1), { staleTime: 30000, retry: false })
  // react-query trata un 404 como error, que es justo lo que queremos
  // detectar: "este entorno todavía no tiene el endpoint".
  const needsFallback = featuresQuery.isError || (!featuresQuery.isLoading && !featuresQuery.data?.features)

  // retry: false TAMBIÉN en los individuales, por el mismo motivo. El caso
  // real que lo exige: el frontend llega a prod ANTES que el api — /features
  // da 404 (fallback activado) y /odontograma/enabled TAMBIÉN da 404 (el
  // router de esa feature no existe todavía en ese entorno). Sin esto, cada
  // apertura de HCE pagaría 3 reintentos extra contra un 404 que no se
  // arregla reintentando. Fail-closed: el flag queda false y el botón no
  // aparece — exactamente lo esperado en esa ventana de deploy.
  const arcaQuery = useQuery(window._l('/arca/enabled').substr(1), { staleTime: 30000, enabled: needsFallback, retry: false })
  const hceQuery = useQuery(window._l('/hce-summary/enabled').substr(1), { staleTime: 30000, enabled: needsFallback, retry: false })
  const odoQuery = useQuery(window._l('/odontograma/enabled').substr(1), { staleTime: 30000, enabled: needsFallback, retry: false })
  const percQuery = useQuery(window._l('/percentiles/enabled').substr(1), { staleTime: 30000, enabled: needsFallback, retry: false })
  const escribaQuery = useQuery(window._l('/escriba/enabled').substr(1), { staleTime: 30000, enabled: needsFallback, retry: false })

  if (needsFallback) {
    return {
      arca: arcaQuery.data?.enabled === true,
      hceSummary: hceQuery.data?.enabled === true,
      odontograma: odoQuery.data?.enabled === true,
      escriba: escribaQuery.data?.enabled === true,
      // El dictado por voz NO tiene endpoint individual: es un sub-flag del
      // odontograma y sólo viaja en /features. En la ventana de deploy donde
      // /features todavía no existe queda apagado — fail-closed, que para una
      // sub-feature beta es exactamente lo que se quiere (el odontograma sigue
      // funcionando; lo único que falta es el botón de dictar).
      odontogramaVoice: false,
      percentiles: percQuery.data?.enabled === true,
      // Mismo criterio que odontogramaVoice: sin endpoint individual, sin
      // /features no hay forma de saber el estado real -> fail-closed. No
      // agregar /web-ui/enabled solo para esta ventana de deploy: agregaría
      // un request extra en el camino feliz por un caso que se resuelve solo
      // en el próximo deploy del api.
      webUi: false,
      webUiAvailable: false,
      isLoading: arcaQuery.isLoading || hceQuery.isLoading || odoQuery.isLoading || percQuery.isLoading || escribaQuery.isLoading,
      viaFallback: true,
    }
  }

  const features = featuresQuery.data?.features || {}
  return {
    arca: features.arca === true,
    hceSummary: features.hce_summary === true,
    odontograma: features.odontograma === true,
    escriba: features.escriba === true,
    // Sub-flag: el backend ya lo devuelve con fail-closed cruzado (voz
    // prendida sin odontograma = apagada), así que acá no hace falta
    // re-cruzarlo — pero el && se deja igual porque el consumidor no debe
    // depender de que un endpoint remoto mantenga ese invariante.
    odontogramaVoice: features.odontograma === true && features.odontograma_voz === true,
    percentiles: features.percentiles === true,
    // web_ui = team habilitado (Igor) && el propio usuario optó. Ver
    // UI_REFACTOR_PLAN.md §6/§14 — todavía SIN lógica de redirect: web no
    // tiene pantallas reales más allá de placeholders, así que exponer el
    // flag acá es aditivo puro, sin ningún consumidor todavía.
    webUi: features.web_ui === true,
    // Distinto de webUi: team habilitado aunque el usuario no haya optado
    // — es lo que necesita el banner "conocé la nueva UI" para distinguir
    // "no está disponible" de "está disponible pero no optaste".
    webUiAvailable: features.web_ui_available === true,
    isLoading: featuresQuery.isLoading,
    viaFallback: false,
  }
}
